/**
 * SupervisorSignOffControl — approve / reject a pending operation counter-signature, wherever the
 * supervisor happens to find it.
 *
 * ux-production-supervisor #1 and #6. Two problems, one control:
 *
 *  1. The counter-signature was only actionable on `/operation-approval`, a route in no menu, that
 *     nothing linked to. Every path that should have led there dead-ended — the live monitor opened
 *     the *recipe editor*, and the batch drill-down rendered the pending approval as grey
 *     `<Descriptions>` with no button. So this is a component, not a page: it goes on the queue, on
 *     the batch drill-down, and in the monitor's drawer, and the approval is actionable at all three.
 *  2. Approve was `submitReview(id, "approve", "")` — one unguarded click — while Reject demanded a
 *     written reason. A GMP counter-signature is the act that lets product move; it is now at least
 *     as deliberate as refusing one: a reason with a floor and a Part 11 re-authentication, the same
 *     shape the MBMR lane already ships for return-for-correction.
 *
 * EXM-00 S1: the counter-signature is ONE request. The password travels with the decision as
 * `esign: {username, password, comment}` (`ApprovalSignModal` + `postAct`); the server verifies it,
 * signs as the logged-in person, refuses the requester / performer of the work (separation of duties)
 * and anyone without the `signoff.countersign` policy. There is no verify-then-post and no `signed_by`.
 */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Descriptions, Space, Tag, Typography } from 'antd';
import { CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import ApprovalSignModal, { actErrorMessage, postAct } from '@/pages/Batch-Processing/Recipe/ApprovalSignModal';
import { formatDateTime } from '@/utils/datetime';
import { invalidateSignoffQueue } from '@/components/signoff/signoffQueueStore';

const { Text } = Typography;

/**
 * Hours a signature has been waiting — the number that makes an aged queue feel aged.
 * EXV-010: the server measures the wait (`waiting_seconds`, on its own clock); pass the approval row. A bare
 * timestamp (an older payload) is still read, against this browser's clock.
 */
export const waitingHours = (rowOrIso) => {
  if (rowOrIso && typeof rowOrIso === 'object') {
    if (Number.isFinite(rowOrIso.waiting_seconds)) return rowOrIso.waiting_seconds / 3600;
    return waitingHours(rowOrIso.requested_at);
  }
  const iso = rowOrIso;
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 3600000;
};

export const waitingLabel = (rowOrIso) => {
  const h = waitingHours(rowOrIso);
  if (h == null) return '—';
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} days`;
};

export default function SupervisorSignOffControl({
  approval,
  size = 'small',
  layout = 'buttons', // 'buttons' | 'block'
  onReviewed,
}) {
  const [mode, setMode] = useState(null); // 'approve' | 'reject'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // EXV-039: one request per signature — a second Enter (or click) while the first is in flight does nothing,
  // even before `busy` has re-rendered into the modal.
  const inFlight = useRef(false);

  if (!approval?.approval_id || String(approval.status || '').toUpperCase() !== 'PENDING') return null;

  const op = approval.operation || {};
  const opName = op.name || approval.operation_name || '—';
  const opNumber = op.number ?? approval.operation_number;

  const submit = async ({ username, password, comment }) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const res = await postAct('supervisor_review_operation', {
        approval_id: approval.approval_id,
        action: mode,
        comment,
        esign: { username, password, comment },
      });
      toast.success(res?.message || (mode === 'approve' ? 'Operation approved and signed.' : 'Operation rejected; batch terminated.'));
      const done = mode;
      setMode(null);
      invalidateSignoffQueue(); // EXV-037: the chip and every queue view re-read the server's count
      onReviewed?.(done, res);
    } catch (err) {
      setError(actErrorMessage(err, 'Could not submit the review.'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const context = (
    <Descriptions size="small" column={1} bordered>
      <Descriptions.Item label="Batch">{approval.batch_number || '—'}</Descriptions.Item>
      <Descriptions.Item label="Operation">
        {opNumber != null ? `#${opNumber} ` : ''}
        {opName}
        {op.type ? <Tag style={{ marginLeft: 6 }}>{op.type}</Tag> : null}
      </Descriptions.Item>
      <Descriptions.Item label="Completed by">
        {approval.requested_by || '—'}
        {approval.requested_at ? ` · ${formatDateTime(approval.requested_at)}` : ''}
      </Descriptions.Item>
      <Descriptions.Item label="Waiting">
        <Text type={waitingHours(approval) > 24 ? 'danger' : undefined} strong>
          {waitingLabel(approval)}
        </Text>
      </Descriptions.Item>
      {approval.machineName ? <Descriptions.Item label="Equipment">{approval.machineName}</Descriptions.Item> : null}
    </Descriptions>
  );

  const buttons =
    layout === 'block' ? (
      <Space>
        <Button
          type="primary"
          size={size}
          icon={<CheckCircle2 size={14} style={{ verticalAlign: -2 }} />}
          style={{ backgroundColor: '#16a34a', borderColor: '#16a34a' }}
          onClick={() => { setError(null); setMode('approve'); }}
        >
          Approve &amp; sign
        </Button>
        <Button danger size={size} icon={<XCircle size={14} style={{ verticalAlign: -2 }} />} onClick={() => { setError(null); setMode('reject'); }}>
          Reject
        </Button>
      </Space>
    ) : (
      <Space>
        <Button
          type="primary"
          size={size}
          icon={<CheckCircle2 size={14} style={{ verticalAlign: -2 }} />}
          style={{ backgroundColor: '#16a34a', borderColor: '#16a34a' }}
          onClick={(e) => { e.stopPropagation(); setError(null); setMode('approve'); }}
        >
          Approve
        </Button>
        <Button danger size={size} icon={<XCircle size={14} style={{ verticalAlign: -2 }} />} onClick={(e) => { e.stopPropagation(); setError(null); setMode('reject'); }}>
          Reject
        </Button>
      </Space>
    );

  return (
    <>
      {buttons}
      <ApprovalSignModal
        open={mode === 'approve'}
        title="Counter-sign this operation"
        action="approve"
        meaning="Approving completes the step and lets the operations that depend on it start."
        requireReason
        reasonLabel="What are you confirming?"
        busy={busy}
        usernameReadOnly
        refusal={error}
        onCancel={() => setMode(null)}
        onSubmit={submit}
      >
        {context}
        {error ? <Text type="danger">{error}</Text> : null}
      </ApprovalSignModal>
      <ApprovalSignModal
        open={mode === 'reject'}
        title="Reject this operation"
        action="reject"
        meaning="Rejecting terminates the batch. This cannot be undone."
        requireReason
        reasonLabel="Reason for rejection"
        busy={busy}
        usernameReadOnly
        refusal={error}
        onCancel={() => setMode(null)}
        onSubmit={submit}
      >
        {context}
        {error ? <Text type="danger">{error}</Text> : null}
      </ApprovalSignModal>
    </>
  );
}

SupervisorSignOffControl.propTypes = {
  approval: PropTypes.object,
  size: PropTypes.string,
  layout: PropTypes.oneOf(['buttons', 'block']),
  onReviewed: PropTypes.func,
};
